import { useMatches } from "@tanstack/react-router";
import { CheckSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSelectionMode } from "@/components/records/selection-mode/selection-mode-provider";
import { useOnlineStatus } from "@/offline/online-status";

/**
 * Right-action slot content for [[project-header.spec]] — the Select / Cancel
 * button that toggles selection mode. (The photos grid/list toggle is not
 * here: it sits in the page's title row, see [[page-title-row.spec]].)
 *
 * Only renders on the records sub-route (`/projects/$projectId/records`)
 * per [[selection-mode.spec]] § Layout. On other project pages (plans,
 * reports) the header right-slot stays empty. The component takes no
 * props; route awareness comes from `useMatches()`.
 */
export function ProjectHeaderRecordsAction() {
  const matches = useMatches();
  const currentPath = matches[matches.length - 1]?.fullPath ?? "";

  // TanStack's `fullPath` is the route template (with trailing slash) so
  // `startsWith` covers exact match + any future nested sub-routes.
  const isOnRecordsPage = currentPath.startsWith(
    "/projects/$projectId/records/",
  );
  if (!isOnRecordsPage) return null;

  return <RecordsActions />;
}

/**
 * Split out so the hooks below only run while the records page is the active
 * route — the early return above is a route guard, not a conditional hook.
 */
function RecordsActions() {
  const {
    isSelectionMode,
    isReportMode,
    enterSelectionMode,
    exitSelectionMode,
  } = useSelectionMode();
  const isOnline = useOnlineStatus();

  if (isSelectionMode) {
    // Report mode has one Cancel, the bulk bar's: it also ends the session
    // where it started, which this one never did. See [[selection-mode.spec]]
    // § Report mode.
    if (isReportMode) return null;
    return (
      <Button variant="ghost" size="sm" onClick={exitSelectionMode}>
        Cancel
      </Button>
    );
  }

  // Selection mode is unavailable offline — hide the Select button so there's
  // no dead control. See offline-availability.md.
  if (!isOnline) return null;

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => enterSelectionMode()}
      aria-label="Enter selection mode"
    >
      <CheckSquare className="h-4 w-4" />
      Select
    </Button>
  );
}
